import { FeatureFlag } from '@parsleyai/feature-flags'
import { createContext, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useFeatureFlag } from '../featureFlags/useFeatureFlag'
import { PreferenceDoc } from '../userData/preferences/types'
import { useGetPreferences } from '../userData/preferences/useGetPreferences'
import { useUserData } from '../userData/useUserData'

// How long into a signed-in session before the feedback button is pointed out.
export const FEEDBACK_NUDGE_DELAY_MS = 30_000

export interface FeedbackNudgeContextValue {
  /** The delay has elapsed for a user who hasn't acknowledged the nudge; the visible trigger should show it. */
  due: boolean
  open: boolean
  show: () => void
  /** Closes the nudge and records it as seen. Only a deliberate user action may call this. */
  acknowledge: () => void
}

export const FeedbackNudgeContext = createContext<FeedbackNudgeContextValue | undefined>(undefined)

/**
 * The once-ever nudge that introduces the header feedback button. Owns the session timer and the
 * seen-watermark; the header `FeedbackTrigger` renders it. Lives app-wide because pages remount the header.
 */
export function FeedbackNudgeProvider({ children }: { children: ReactNode }) {
  const { uid } = useUserData()
  const enabled = useFeatureFlag(FeatureFlag.FeedbackNudge)
  const { state, updatePreferences } = useGetPreferences()
  const [due, setDue] = useState(false)
  const [open, setOpen] = useState(false)
  // Covers the gap before our own write echoes back through the snapshot.
  const [seenLocally, setSeenLocally] = useState(false)

  const seen = seenLocally || state.settings?.feedbackNudgeSeenAt !== undefined
  const eligible = Boolean(uid) && enabled && !state.loading && !seen

  useEffect(() => {
    if (uid) return
    setDue(false)
    setOpen(false)
    setSeenLocally(false)
  }, [uid])

  useEffect(() => {
    if (!eligible) return
    const timer = setTimeout(() => setDue(true), FEEDBACK_NUDGE_DELAY_MS)
    return () => clearTimeout(timer)
  }, [eligible])

  // Opening writes nothing: until the user acts on it, the nudge comes back next session.
  const show = useCallback(() => {
    if (eligible) setOpen(true)
  }, [eligible])

  const acknowledge = useCallback(() => {
    setOpen(false)
    if (!eligible) return
    setSeenLocally(true)
    void updatePreferences(PreferenceDoc.Settings, { feedbackNudgeSeenAt: new Date().toISOString() })
  }, [eligible, updatePreferences])

  const value = useMemo(
    () => ({ due: due && eligible && !open, open: open && eligible, show, acknowledge }),
    [due, eligible, open, show, acknowledge]
  )

  return <FeedbackNudgeContext.Provider value={value}>{children}</FeedbackNudgeContext.Provider>
}
